import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
} from '@mui/material';
import { ChevronRight } from 'lucide-react';

export default function BotCatalogCard({
  bot,
  botStatus,
  isActivated,
  onSelect,
}) {
  const isComingSoon = bot.status === 'coming_soon';
  const effectiveStatus = botStatus || (isActivated ? 'active' : 'unactivated');

  const renderStatusPill = () => {
    if (isComingSoon) {
      return (
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: '12px',
            bgcolor: '#F3F4F6',
            color: '#6B7280',
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          Coming Soon
        </Box>
      );
    }

    if (effectiveStatus === 'active') {
      return (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.5,
            borderRadius: '12px',
            bgcolor: '#ECFDF5',
            color: '#059669',
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#10B981',
            }}
          />
          Active
        </Box>
      );
    }

    if (effectiveStatus === 'setting_up') {
      return (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.5,
            borderRadius: '12px',
            bgcolor: '#FEF3C7',
            color: '#B45309',
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#F59E0B',
              boxShadow: '0 0 0 2px rgba(245, 158, 11, 0.3)',
            }}
          />
          Setting Up
        </Box>
      );
    }

    if (effectiveStatus === 'suspended') {
      return (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.5,
            borderRadius: '12px',
            bgcolor: '#FEF2F2',
            color: '#DC2626',
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#EF4444',
            }}
          />
          Suspended
        </Box>
      );
    }

    return (
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          borderRadius: '12px',
          bgcolor: '#F1F5F9',
          color: '#475569',
          fontSize: '0.75rem',
          fontWeight: 600,
        }}
      >
        Available
      </Box>
    );
  };

  const getStatusNote = () => {
    if (isComingSoon) {
      return 'In development';
    }
    if (effectiveStatus === 'active') {
      return 'Ready for orders';
    }
    if (effectiveStatus === 'setting_up') {
      return 'Super admin provisioning';
    }
    if (effectiveStatus === 'suspended') {
      return 'Overdue subscription';
    }
    return 'Tap to set up';
  };

  const getButtonText = () => {
    if (isComingSoon) {
      return 'Unavailable';
    }
    if (effectiveStatus === 'active') {
      return 'Configure';
    }
    if (effectiveStatus === 'setting_up') {
      return 'View Setup';
    }
    if (effectiveStatus === 'suspended') {
      return 'Reactivate';
    }
    return 'Set Up';
  };

  const getButtonSx = () => {
    if (isComingSoon) {
      return {
        bgcolor: 'transparent',
        color: '#9CA3AF',
      };
    }
    if (effectiveStatus === 'active') {
      return {
        bgcolor: '#F3F4F6',
        color: '#111827',
        '&:hover': { bgcolor: '#E5E7EB', boxShadow: 'none' },
      };
    }
    if (effectiveStatus === 'setting_up') {
      return {
        bgcolor: '#FEF3C7',
        color: '#92400E',
        '&:hover': { bgcolor: '#FDE68A', boxShadow: 'none' },
      };
    }
    if (effectiveStatus === 'suspended') {
      return {
        bgcolor: '#DC2626',
        color: '#FFFFFF',
        '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
      };
    }
    return {
      bgcolor: '#111827',
      color: '#FFFFFF',
      '&:hover': { bgcolor: '#1F2937', boxShadow: 'none' },
    };
  };

  const hoverEffect = isComingSoon
    ? {}
    : {
        transform: 'translateY(-2px)',
        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.06)',
        borderColor: 'rgba(0, 0, 0, 0.15)',
      };

  return (
    <Paper
      elevation={0}
      onClick={() => !isComingSoon && onSelect(bot.id)}
      sx={{
        p: 3,
        borderRadius: '20px',
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.08)',
        bgcolor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        minHeight: 220,
        cursor: isComingSoon ? 'default' : 'pointer',
        transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
        '&:hover': hoverEffect,
      }}
    >
      <Stack spacing={2}>
        {/* Top: Icon + Status Pill */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '14px',
              bgcolor: bot.brandBg,
              color: bot.brandColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 14px ${bot.brandColor}22`,
            }}
          >
            {bot.icon}
          </Box>

          {renderStatusPill()}
        </Stack>

        {/* Title & Concise Summary */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1.15rem',
              color: '#111827',
              letterSpacing: '-0.015em',
            }}
          >
            {bot.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#6B7280',
              mt: 0.75,
              fontSize: '0.875rem',
              lineHeight: 1.5,
            }}
          >
            {bot.description}
          </Typography>
        </Box>
      </Stack>

      {/* Bottom Action - Apple Pill Style */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pt: 2.5, mt: 1, borderTop: '1px solid', borderColor: 'rgba(0, 0, 0, 0.05)' }}
      >
        <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 500 }}>
          {getStatusNote()}
        </Typography>

        <Button
          size="small"
          disabled={isComingSoon}
          onClick={(e) => {
            e.stopPropagation();
            if (!isComingSoon) onSelect(bot.id);
          }}
          endIcon={!isComingSoon && <ChevronRight size={14} />}
          sx={{
            px: 2,
            py: 0.6,
            borderRadius: '20px',
            fontSize: '0.8rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            ...getButtonSx(),
          }}
        >
          {getButtonText()}
        </Button>
      </Stack>
    </Paper>
  );
}

BotCatalogCard.propTypes = {
  bot: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    description: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    brandColor: PropTypes.string.isRequired,
    brandBg: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
  }).isRequired,
  botStatus: PropTypes.oneOf(['unactivated', 'setting_up', 'active', 'suspended']),
  isActivated: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};
